
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'

import TemplatesListFilters from '@/modules/ContractsModule/components/TemplatesListFilters/index.vue'

import { DataState } from '@/core/classes/DataState.class'

import useNotifications from '@/common/composable/useNotifications'
import { contracts } from '@/core/realisations/contracts.realisation'
import { GetTemplatesParams, Template } from '@/core/types/contracts.types'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useFormData } from '@/common/composable/useFormData'

import { formatDateWithTimeFromBackend } from '@/common/helpers/formatDate'

export const TemplatesColumns = [
    {
        name: '№',
        key: 'id',
    },
    {
        name: 'Наименование',
        key: 'name',
    },
    {
        name: 'Дата добавления',
        key: 'createdAt',
    },
    // {
    //     name: 'Шаблон',
    //     key: 'url',
    // },
    {
        name: 'Кто добавил',
        key: 'userName',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TheButton,
        TemplatesListFilters,
        TheTable,
        TableScrollWrapper,
        TheInput,
        Modal,
        FileReader,
        ButtonDropdown,
    },
    setup() {
        const { getFormData } = useFormData()

        const state = reactive({
            is_add: false,
            is_edit: false,
            template_id: null as number | null,
            is_template_loading: false,
            template_name: null as string | null,
            template_file: [] as File[],
        })

        const data_state = reactive(new DataState<Template>({}))

        const filters_state = reactive({
            limit: 50,
            //
            name: null as string | null,
            user_id: null as number | null,
            from_created_at: null as string | null,
            to_created_at: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                template_name: { required },
                template_file: { required },
            },
            state
        )

        const notifications = useNotifications()

        const loadTemplates = async (page: number) => {
            data_state.pagination.current_page = page

            try {
                data_state.is_loading = true

                let params = {
                    page,
                    limit: filters_state.limit,
                    name: filters_state.name,
                    user_id: filters_state.user_id,
                    from_created_at: filters_state.from_created_at,
                    to_created_at: filters_state.to_created_at,
                }

                const response = await contracts.getTemplates(params)
                data_state.list = response.data
                data_state.pagination.total_pages = response.meta.last_page
                data_state.pagination.total_items = response.meta.total

                data_state.is_failed = false
                data_state.is_completed = true
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadTemplates(data_state.pagination.current_page)
        }

        const applyFilters = (params: GetTemplatesParams) => {
            filters_state.name = params.name || null
            filters_state.user_id = params.user_id || null
            filters_state.from_created_at = params.from_created_at || null
            filters_state.to_created_at = params.to_created_at || null

            loadTemplates(data_state.pagination.current_page)
        }

        const resetFilters = () => {
            filters_state.name = null
            filters_state.user_id = null
            filters_state.from_created_at = null
            filters_state.to_created_at = null

            loadTemplates(data_state.pagination.current_page)
        }

        const resetTemplate = () => {
            state.is_add = false
            state.is_edit = false
            state.is_template_loading = false
            state.template_name = null
            state.template_file = []
            state.template_id = null
        }

        const addTemplate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_template_loading = true

                const payload = {
                    template_name: state.template_name as string,
                    template_file: state.template_file[0] as File,
                }
                await contracts.addTemplate(getFormData(payload))

                notifications.notify({ title: 'Шаблон успешно создан!', type: 'success' })
                loadTemplates(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении шаблона произошла ошибка',
                })
            } finally {
                resetTemplate()
            }
        }

        const editTemplate = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !state.template_id) return

            try {
                state.is_template_loading = true

                const payload = {
                    template_name: state.template_name as string,
                    template_file: state.template_file[0] as File,
                }
                await contracts.editTemplate(state.template_id, getFormData(payload))

                notifications.notify({ title: 'Шаблон успешно создан!', type: 'success' })
                loadTemplates(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении шаблона произошла ошибка',
                })
            } finally {
                resetTemplate()
            }
        }

        const manageTemplate = () => {
            if (state.is_edit) editTemplate()
            else addTemplate()
        }

        const deleteTemplate = async (row: Template) => {
            const answer = confirm(`Вы действительно хотите удалить шаблон '${row.id}'?`)
            if (!answer) return

            try {
                data_state.is_loading = true

                await contracts.deleteTemplate(row.id)
                notifications.notify({
                    title: `Шаблон '${row.id}' удален`,
                    type: 'success',
                })

                loadTemplates(1)
            } catch (error) {
                notifications.notifyError({ error, title: 'При удалении шаблона произошла ошибка' })
            } finally {
                data_state.is_loading = false
            }
        }

        const onEditTemplate = (template: Template) => {
            state.is_add = true
            state.is_edit = true
            state.template_name = template.name
            state.template_id = template.id
        }

        const modalTitle = computed(() =>
            state.is_edit ? 'Редактировать шаблон' : 'Добавить шаблон'
        )

        onBeforeMount(() => loadTemplates(1))

        return {
            state,
            filters_state,
            data_state,
            state_form$,
            TemplatesColumns,
            loadTemplates,
            applyFilters,
            resetFilters,
            deleteTemplate,
            onLimitChange,
            addTemplate,
            onEditTemplate,
            manageTemplate,
            modalTitle,
            resetTemplate,
            formatDateWithTimeFromBackend,
        }
    },
})
